/**
 * Clears a single value from a cell object based on its properties.
 *
 * @param cell - The cell object to be cleared. It may contain properties such as `unit`, `utcOnly`, or `class`.
 * @returns The cleared value, which can be a string, object, or the original value depending on the cell's properties.
 */
export const clearSingleValue = (cell: any) => {
  let value: any = "";
  if (cell.unit === "temperature") {
    value = {
      value: "",
      unit: "celsius",
    };
  } else if (cell.unit === "pressure") {
    value = {
      value: "",
      unit: "mbar",
    };
  } else if (cell.utcOnly) {
    value = {
      type: "UTC",
      time: null,
    };
  } else if (cell.unit === "time") {
    value = {
      time: null,
      type: "LT",
    };
  } else if (cell.unit === "24lock") {
    value = {
      time: "",
      type: "col",
    };
  } else if (cell.class === "logResuls") {
    return cell.value;
  }
  return value;
};

/**
 * Clears NMEA-specific properties from a column object
 *
 * @param col - The column object to clear NMEA properties from
 * @returns The column object without NMEA properties
 */
export const clearNMEAProperties = (
  col: Record<string, any>,
): Record<string, any> => {
  const cleanCol = { ...col };
  delete cleanCol.NMEAId;
  delete cleanCol.NMEAValue;
  delete cleanCol.source;
  return cleanCol;
};

/**
 * Recursively clears values from an object, optionally skipping a specific index.
 *
 * @param obj - The object whose values need to be cleared.
 * @param isList - A boolean indicating whether the object is a list.
 * @param copyPrevVal - (Optional) A number indicating the index to skip while clearing values.
 * @param forceFullClear - (Optional) A boolean to force complete clearing even if copyPrevVal is set.
 * @returns A new object with cleared values.
 */
export const clearValues = (
  obj: Record<string, any>,
  isList: boolean,
  copyPrevVal: number | undefined,
  forceFullClear: boolean = false,
): Record<string, any> => {
  const newObj = structuredClone(obj);
  if ("isSingleRow" in newObj) {
    delete newObj.isSingleRow;
  }

  Object.keys(newObj).forEach((key, index) => {
    // Skip copying if copyPrevVal is set, unless this is a forced clear or dealing with NMEA properties
    if (
      typeof copyPrevVal === "number" &&
      index === copyPrevVal &&
      !forceFullClear
    ) {
      // Even when copying, always clear NMEA properties for manual entries
      if (key === "NMEAId" || key === "NMEAValue" || key === "source") {
        delete newObj[key];
      }
      return;
    }
    if (key === "portCode") {
      delete newObj[key];
    }
    if (newObj[key]?.location) {
      delete newObj[key].location;
    }
    if (
      key !== "value" &&
      typeof newObj[key] === "object" &&
      newObj[key] !== null &&
      !isList
    ) {
      newObj[key] = clearValues(newObj[key], true, copyPrevVal, forceFullClear);
    } else if (key === "value") {
      newObj[key] = clearSingleValue(newObj);
    } else if (key === "unit" && newObj[key] === "sub" && newObj.content) {
      newObj.content.cols = [];
    }

    // Remove NMEA-specific properties when clearing for manual entry
    if (key === "NMEAId" || key === "NMEAValue" || key === "source") {
      delete newObj[key];
    }
  });
  return newObj;
};
